/**
 * @module features/entity/entity-types
 * @description Entity types configuration and helper functions for all entity types in the application
 */

/**
 * Entity types configuration object
 * Defines the properties and behaviors for each entity type in the application
 *
 * Each entity type supports the following properties:
 *
 * Core Properties:
 * - singular: Lowercase singular form (e.g., 'project') - used in templates and labels
 * - label: Display name singular (e.g., 'Project') - primary display label
 * - labelPlural: Display name plural (e.g., 'Projects') - used in navigation breadcrumbs
 * - icon: Emoji icon for visual identification
 * - paramKey: URL parameter name (e.g., 'projectId') - used in query strings
 * - insightlyUrl: Base URL for Insightly entity detail pages
 * - hasSudbId: Boolean - whether this entity type has SUDB ID field
 *
 * Search Page Properties (entity type selection and search):
 * - searchTitle: Page title when viewing this entity type (default: '{labelPlural} Search')
 * - searchIntro: Description text below title (default: 'Search for a {singular} to view archived files')
 * - searchButtonText: Search button label (default: 'Search {labelPlural}')
 * - searchPlaceholder: Search input placeholder text
 *
 * Entity Detail/Metadata Properties (when viewing specific entity):
 * - metadataTitle: Header for metadata section (default: '{label} Information')
 * - metadataCurrentLabel: Label for entity name field (default: 'Current {label}')
 * - metadataIdLabel: Label for Insightly ID field (default: 'Insightly {label} ID')
 *
 * Special Properties (optional, used for entity-specific behavior):
 * - searchEntityType: Routes to different backend (e.g., 'dc-photos' routes to 'opportunities')
 * - defaultCategory: Pre-fills this category when uploading files (e.g., 'dc-photo')
 * - categoryFilter: Filters file list to show only this category (e.g., 'DC PHOTO')
 * - emphasizeUpload: Boolean - applies special styling to upload button
 * - color: Hex color for custom entity branding (e.g., '#ff6b35' for DC-Photos orange)
 *
 * Note: Most entity types only use core, search, and metadata properties.
 * Special properties are primarily used by dc-photos which proxies to opportunities.
 *
 * @constant {Object} ENTITY_TYPES
 * @property {Object} projects - Project entity configuration
 * @property {Object} opportunities - Opportunity entity configuration
 * @property {Object} leads - Lead entity configuration
 * @property {Object} contacts - Contact entity configuration
 * @property {Object} dc-photos - DC Photos entity configuration (proxies to opportunities)
 */
export const ENTITY_TYPES = {
    projects: {
        singular: 'project',
        label: 'Project',
        labelPlural: 'Projects',
        searchTitle: 'Project Search',
        searchIntro: 'Search for a project to view archived files',
        searchButtonText: 'Search Projects',
        paramKey: 'projectId',
        insightlyUrl: 'https://crm.na1.insightly.com/details/project/',
        searchPlaceholder: 'Search for a project',
        icon: '📁',
        hasSudbId: true,
        // Metadata display labels
        metadataTitle: 'Project Information',
        metadataCurrentLabel: 'Current Project',
        metadataIdLabel: 'Insightly Project ID'
    },
    opportunities: {
        singular: 'opportunity',
        label: 'Opportunity',
        labelPlural: 'Opportunities',
        searchTitle: 'Opportunity Search',
        searchIntro: 'Search for an opportunity to view archived files',
        searchButtonText: 'Search Opportunities',
        paramKey: 'opportunityId',
        insightlyUrl: 'https://crm.na1.insightly.com/details/opportunity/',
        searchPlaceholder: 'Search for an opportunity',
        icon: '💼',
        hasSudbId: false,
        // Metadata display labels
        metadataTitle: 'Opportunity Information',
        metadataCurrentLabel: 'Current Opportunity',
        metadataIdLabel: 'Insightly Opportunity ID'
    },
    leads: {
        singular: 'lead',
        label: 'Lead',
        labelPlural: 'Leads',
        searchTitle: 'Lead Search',
        searchIntro: 'Search for a lead to view archived files',
        searchButtonText: 'Search Leads',
        paramKey: 'leadId',
        insightlyUrl: 'https://crm.na1.insightly.com/details/lead/',
        searchPlaceholder: 'Search for a lead',
        icon: '🎯',
        hasSudbId: false,
        // Metadata display labels
        metadataTitle: 'Lead Information',
        metadataCurrentLabel: 'Current Lead',
        metadataIdLabel: 'Insightly Lead ID'
    },
    contacts: {
        singular: 'contact',
        label: 'Contact',
        labelPlural: 'Contacts',
        searchTitle: 'Contact Search',
        searchIntro: 'Search for a contact to view archived files',
        searchButtonText: 'Search Contacts',
        paramKey: 'contactId',
        insightlyUrl: 'https://crm.na1.insightly.com/details/contact/',
        searchPlaceholder: 'Search for a contact',
        icon: '👤',
        hasSudbId: false,
        // Metadata display labels
        metadataTitle: 'Contact Information',
        metadataCurrentLabel: 'Current Contact',
        metadataIdLabel: 'Insightly Contact ID'
    },
    'dc-photos': {
        singular: 'photo upload',
        label: 'DC-Photos',
        labelPlural: 'DC-Photos',
        searchTitle: 'Opportunity Search for DC Photos',
        searchIntro: 'Upload DC Photos to Insightly Opportunity',
        searchButtonText: 'Search Opportunity',
        paramKey: 'opportunityId',
        insightlyUrl: 'https://crm.na1.insightly.com/details/opportunity/',
        searchPlaceholder: 'Search for an opportunity',
        icon: '📸',
        color: '#b7d4f5ff',
        hasSudbId: false,
        // Special behavior: proxies to opportunities backend
        searchEntityType: 'opportunities',
        defaultCategory: 'dc-photo',
        categoryFilter: 'DC PHOTO',
        emphasizeUpload: true,
        // Metadata display labels - shows as "Opportunity" to users, not "DC-Photos"
        metadataTitle: 'Opportunity Information',
        metadataCurrentLabel: 'Current Opportunity',
        metadataIdLabel: 'Insightly Opportunity ID'
    }
};

/**
 * Get entity type configuration by entity type key
 * @param {string} entityType - The entity type key (projects, opportunities, leads, contacts)
 * @returns {Object|null} Entity configuration object or null if not found
 */
export function getEntityConfig(entityType) {
    return ENTITY_TYPES[entityType] || null;
}

/**
 * Get all entity type keys
 * @returns {string[]} Array of entity type keys
 */
export function getEntityTypeKeys() {
    return Object.keys(ENTITY_TYPES);
}

/**
 * Check if entity type exists
 * @param {string} entityType - The entity type key to check
 * @returns {boolean} True if entity type exists
 */
export function isValidEntityType(entityType) {
    return entityType in ENTITY_TYPES;
}

/**
 * Get entity type from URL parameter
 * Checks URL parameters for any entity type ID parameter
 * @returns {string|null} Entity type key if found, null otherwise
 */
export function getEntityTypeFromUrl() {
    const params = new URLSearchParams(window.location.search);

    for (const [entityType, config] of Object.entries(ENTITY_TYPES)) {
        if (params.has(config.paramKey)) {
            return entityType;
        }
    }

    return null;
}

/**
 * Get entity ID from URL parameter for a specific entity type
 * @param {string} entityType - The entity type to get ID for
 * @returns {string|null} Entity ID if found, null otherwise
 */
export function getEntityIdFromUrl(entityType) {
    const config = getEntityConfig(entityType);
    if (!config) return null;

    const params = new URLSearchParams(window.location.search);
    return params.get(config.paramKey);
}

/**
 * Build Insightly URL for an entity
 * @param {string} entityType - The entity type
 * @param {string} entityId - The entity ID
 * @returns {string|null} Insightly URL or null if invalid entity type
 */
export function buildInsightlyUrl(entityType, entityId) {
    const config = getEntityConfig(entityType);
    if (!config) return null;

    return `${config.insightlyUrl}${entityId}`;
}

/**
 * Build SUDB URL for an entity (if applicable)
 * @param {string} sudbId - The SUDB ID
 * @returns {string} SUDB URL
 */
export function buildSudbUrl(sudbId) {
    return `https://orders.shuttersup.co.uk/Order/DatabaseId/${sudbId}`;
}

/**
 * Format entity label for display
 * @param {string} entityType - The entity type
 * @param {boolean} plural - Whether to use plural form
 * @returns {string} Formatted label
 */
export function formatEntityLabel(entityType, plural = false) {
    const config = getEntityConfig(entityType);
    if (!config) return 'Entity';

    return plural ? `${config.label}s` : config.label;
}

/**
 * Get entity icon
 * @param {string} entityType - The entity type
 * @returns {string} Entity icon emoji
 */
export function getEntityIcon(entityType) {
    const config = getEntityConfig(entityType);
    return config ? config.icon : '📎';
}

export default ENTITY_TYPES;
